import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';

import { SectorsService } from '../../sectors/sectors.service';
import { BranchNames, SectorNames } from '../../sectors/sectors';
import { NormativesService } from '../normatives.service';
import { ISectorSpeedStandard, ISpeedNormativeRow, IVehicleTypeOption, Region } from '../normatives';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-speed-normatives',
  templateUrl: './speed-normatives.component.html',
  styleUrls: ['./speed-normatives.component.scss'],
  standalone: false,
})
export class SpeedNormativesComponent implements OnInit, OnDestroy {
  public regions: Region[] = [];
  public branches: BranchNames[] = [];
  public sectors: SectorNames[] = [];
  public vehicleTypes: IVehicleTypeOption[] = [];

  public selectedRegion: Region | null = null;
  public selectedBranch: BranchNames | null = null;
  public selectedSector: SectorNames | null = null;
  public selectedVehicleType: IVehicleTypeOption | null = null;

  public appliedRegion: Region | null = null;
  public appliedBranch: BranchNames | null = null;
  public appliedSector: SectorNames | null = null;
  public appliedVehicleType: IVehicleTypeOption | null = null;

  public rows: ISpeedNormativeRow[] = [];
  // sector_id -> название участка: в нормативах скорости бэкенд отдаёт только sector_id
  public sectorNames: { [sectorId: number]: string } = {};
  public isLoading = false;
  public isFiltersLoading = false;
  public error: string | null = null;
  public saveError: string | null = null;
  public successMessage: string | null = null;

  // Полный список нормативов, строки таблицы строятся из него фильтрацией в памяти
  private allStandards: ISectorSpeedStandard[] = [];
  // Участки применённого фильтра; null — фильтр по территории не задан
  private appliedSectorIds: number[] | null = null;
  private destroyed$ = new Subject<void>();

  constructor(
    private sectorsService: SectorsService,
    private normativesService: NormativesService
  ) {}

  ngOnInit() {
    this.loadRegions();
    this.loadSectorNames();
    this.loadStandards();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onChangeRegion(region: Region | null) {
    this.selectedRegion = region;
    this.selectedBranch = null;
    this.branches = [];
    this.selectedSector = null;
    this.sectors = [];

    if (!region) {
      return;
    }

    this.isFiltersLoading = true;
    this.sectorsService
      .getBranches({ mb_regions: [region.name] })
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (branchesResponse) => {
          this.branches = branchesResponse.data || [];
        },
        () => {
          this.branches = [];
        }
      );
  }

  public onChangeBranch(branch: BranchNames | null) {
    this.selectedBranch = branch;
    this.selectedSector = null;
    this.sectors = [];

    if (!branch) {
      return;
    }

    this.isFiltersLoading = true;
    this.sectorsService
      .getFilterPlots({ mb_ids: [branch.mb_id] })
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (sectorsResponse) => {
          this.mergeSectorNames(sectorsResponse.data);
          this.sectors = this.sectorsService.makeSectorsNumbers(this.getVisibleSectors(sectorsResponse.data));
        },
        () => {
          this.sectors = [];
        }
      );
  }

  public onChangeSector(sector: SectorNames | null) {
    this.selectedSector = sector;
  }

  public onChangeVehicleType(vehicleType: IVehicleTypeOption | null) {
    this.selectedVehicleType = vehicleType;
  }

  public applyFilter() {
    this.appliedRegion = this.selectedRegion;
    this.appliedBranch = this.selectedBranch;
    this.appliedSector = this.selectedSector;
    this.appliedVehicleType = this.selectedVehicleType;
    this.saveError = null;
    this.successMessage = null;

    this.isFiltersLoading = true;
    this.resolveSectorIds()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (sectorIds) => {
          this.appliedSectorIds = sectorIds;
          this.buildRows();
        },
        () => {
          this.appliedSectorIds = [];
          this.buildRows();
        }
      );
  }

  public resetFilter() {
    this.selectedRegion = null;
    this.selectedBranch = null;
    this.selectedSector = null;
    this.selectedVehicleType = null;
    this.branches = [];
    this.sectors = [];

    this.appliedRegion = null;
    this.appliedBranch = null;
    this.appliedSector = null;
    this.appliedVehicleType = null;
    this.appliedSectorIds = null;
    this.saveError = null;
    this.successMessage = null;

    this.buildRows();
  }

  /**
   * Заголовок таблицы отражает применённый уровень фильтра,
   * как отдельные таблицы уровней в «Нормативах работ»
   */
  public getScopeTitle(): string {
    if (this.appliedSector) {
      return `Участок ${this.getSectorName(this.appliedSector.sector_id)}`;
    }
    if (this.appliedBranch) {
      return this.appliedBranch.mb_name;
    }
    if (this.appliedRegion) {
      return this.appliedRegion.name;
    }

    return 'Российская Федерация';
  }

  public loadStandards() {
    this.isLoading = true;
    this.error = null;
    this.saveError = null;
    this.successMessage = null;

    this.normativesService
      .getAllSectorSpeedStandards()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe(
        (standards) => {
          this.allStandards = standards || [];
          this.setVehicleTypes(this.allStandards);
          this.buildRows();
        },
        () => {
          this.allStandards = [];
          this.rows = [];
          this.error = 'Не удалось загрузить нормативы скорости';
        }
      );
  }

  public startEditing(row: ISpeedNormativeRow) {
    if (row.isSaving) {
      return;
    }

    this.saveError = null;
    this.successMessage = null;
    row.isEditing = true;
  }

  public cancelEditing(row: ISpeedNormativeRow) {
    row.value = row.originalValue;
    row.isEditing = false;
  }

  public onValueInput(row: ISpeedNormativeRow, rawValue: string) {
    row.value = rawValue === '' ? null : +rawValue;
  }

  public isValueChanged(row: ISpeedNormativeRow): boolean {
    return row.value !== null && row.value !== row.originalValue;
  }

  public saveRow(row: ISpeedNormativeRow) {
    if (row.isSaving) {
      return;
    }

    if (!this.isValueChanged(row)) {
      this.cancelEditing(row);
      return;
    }

    row.isSaving = true;
    this.saveError = null;
    this.successMessage = null;

    this.normativesService
      .updateSectorSpeedStandard(row.id, row.value)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (row.isSaving = false))
      )
      .subscribe(
        () => {
          row.originalValue = row.value;
          row.isEditing = false;
          this.updateCachedStandardValue(row);
          this.successMessage = `Норматив скорости участка ${this.getSectorName(row.sectorId)} обновлён`;
        },
        () => {
          this.saveError = `Не удалось обновить норматив скорости участка ${this.getSectorName(row.sectorId)}`;
        }
      );
  }

  public getSectorName(sectorId: number): string {
    return this.sectorNames[sectorId] || String(sectorId);
  }

  public formatValue(value: number | null): string {
    return value === undefined || value === null ? '—' : String(value);
  }

  private loadRegions() {
    this.isFiltersLoading = true;
    this.sectorsService
      .getRegions()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (regionsResponse) => {
          this.regions = (regionsResponse.data || []).map((name) => ({ name }));
        },
        () => {
          this.regions = [];
        }
      );
  }

  /**
   * Строки таблицы из загруженного списка.
   *
   * Фильтруем в памяти, а не запросом: при нескольких sector_id в query
   * бэкенд возвращал нормативы только одного участка (дефект ТК-16/ТК-17).
   * Заодно у крупных регионов не растёт длина URL
   */
  private buildRows() {
    const sectorIds = this.appliedSectorIds;
    const allowedSectorIds = sectorIds
      ? sectorIds.reduce(
          (acc, sectorId) => {
            acc[sectorId] = true;
            return acc;
          },
          {} as { [sectorId: number]: boolean }
        )
      : null;
    const vehicleTypeId = this.appliedVehicleType ? this.appliedVehicleType.id : null;

    this.rows = this.allStandards
      .filter((standard) => {
        if (allowedSectorIds && !allowedSectorIds[standard.sector_id]) {
          return false;
        }

        return vehicleTypeId === null || standard.vehicle_type_id === vehicleTypeId;
      })
      .map((standard) => this.toRow(standard));
  }

  /**
   * Справочника средств передвижения на бэкенде нет — собираем из самих нормативов.
   * Берём полный список до фильтрации, поэтому опции не исчезают при фильтре по типу
   */
  private setVehicleTypes(standards: ISectorSpeedStandard[]) {
    const known: { [id: number]: boolean } = {};
    const vehicleTypes: IVehicleTypeOption[] = [];

    (standards || []).forEach((standard) => {
      const vehicleType = standard.vehicle_type;
      const vehicleTypeId = vehicleType && vehicleType.id;

      if (vehicleTypeId && !known[vehicleTypeId]) {
        known[vehicleTypeId] = true;
        vehicleTypes.push({ id: vehicleTypeId, name: vehicleType.description || String(vehicleTypeId) });
      }
    });

    this.vehicleTypes = vehicleTypes.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Сохранённое значение возвращаем и в общий список, иначе после
   * повторного применения фильтра в строке снова окажется старое число
   */
  private updateCachedStandardValue(row: ISpeedNormativeRow) {
    const standard = this.allStandards.find((item) => item && item.id === row.id);

    if (standard) {
      standard.value = row.value;
    }
  }

  private loadSectorNames() {
    this.sectorsService
      .getFilterPlots(null)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (sectorsResponse) => this.mergeSectorNames(sectorsResponse.data),
        () => {
          // Без справочника в столбце «Участок» останется sector_id
        }
      );
  }

  /**
   * Названия берём в исходном виде: makeSectorsNumbers оборачивает
   * скрытые участки в HTML-разметку, для текстовой ячейки она не нужна
   */
  private mergeSectorNames(sectors: SectorNames[]) {
    (sectors || []).forEach((sector) => {
      if (sector.sector_number_str) {
        this.sectorNames[sector.sector_id] = sector.sector_number_str;
      }
    });
  }

  /**
   * Участки применённого фильтра. null — фильтр по территории не задан.
   *
   * В справочник участков ходим только с одним значением параметра:
   * многозначный mb_ids (все филиалы региона) возвращал участки не полностью,
   * поэтому регион спрашиваем через mb_regions одним запросом
   */
  private resolveSectorIds(): Observable<number[] | null> {
    if (this.appliedSector) {
      return of([this.appliedSector.sector_id]);
    }

    if (this.appliedBranch) {
      return this.sectors.length ? of(this.toSectorIds(this.sectors)) : this.loadSectorIds({ mb_ids: [this.appliedBranch.mb_id] });
    }

    if (this.appliedRegion) {
      return this.loadSectorIds({ mb_regions: [this.appliedRegion.name] });
    }

    return of(null);
  }

  private loadSectorIds(params): Observable<number[]> {
    return this.sectorsService.getFilterPlots(params).pipe(
      map((response) => {
        this.mergeSectorNames(response.data);
        return this.toSectorIds(response.data);
      }),
      catchError(() => of([] as number[]))
    );
  }

  private getVisibleSectors(sectors: SectorNames[]): SectorNames[] {
    return (sectors || []).filter((sector) => sector.is_visible !== 0);
  }

  private toSectorIds(sectors: SectorNames[]): number[] {
    return this.getVisibleSectors(sectors).map((sector) => sector.sector_id);
  }

  private toRow(standard: ISectorSpeedStandard): ISpeedNormativeRow {
    return {
      id: standard.id,
      sectorId: standard.sector_id,
      vehicleTypeName: standard.vehicle_type && standard.vehicle_type.description ? standard.vehicle_type.description : String(standard.vehicle_type_id),
      value: standard.value,
      originalValue: standard.value,
      isEditing: false,
      isSaving: false,
    };
  }
}
